import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Clock } from "lucide-react";
import type { Article } from "@/types/blog";

interface FeaturedArticleProps {
  article: Article;
}

const AUTHOR_NAME: Record<Article["frontmatter"]["author"], string> = {
  pinch: "Pinch Guigui",
  ezra: "Ezra Rothchild",
};

export function FeaturedArticle({ article }: FeaturedArticleProps) {
  const { t, i18n } = useTranslation();
  const fm = article.frontmatter;
  const dateLabel = new Date(fm.publishedAt).toLocaleDateString(
    i18n.language === "he" ? "he-IL" : "en-US",
    { year: "numeric", month: "long", day: "numeric" },
  );

  return (
    <Link
      to={`/blog/${fm.slug}`}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 0,
        background: "var(--dyna-bg-elevated)",
        border: "1px solid var(--dyna-border)",
        borderRadius: 20,
        overflow: "hidden",
        textDecoration: "none",
        color: "inherit",
        transition: "border-color 240ms ease, transform 240ms ease",
      }}
      className="dyna-blog-featured"
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--lavender-400)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--dyna-border)";
      }}
    >
      <div
        style={{
          minHeight: 320,
          background: fm.coverImage
            ? `url(${fm.coverImage}) center/cover no-repeat`
            : "linear-gradient(135deg, rgba(90,70,102,0.6), rgba(156,139,194,0.25))",
        }}
      />
      <div
        style={{
          padding: "32px 36px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          justifyContent: "center",
        }}
      >
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: "#9BC289",
              background: "rgba(155,194,137,0.10)",
              padding: "3px 10px",
              borderRadius: 999,
            }}
          >
            Featured
          </span>
          {fm.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                color: "var(--lavender-400)",
                background: "rgba(156,139,194,0.10)",
                padding: "3px 10px",
                borderRadius: 999,
              }}
            >
              {tag}
            </span>
          ))}
        </div>
        <h2
          style={{
            fontFamily: "\"Coolvetica\", system-ui, sans-serif",
            fontSize: "clamp(28px, 3.4vw, 40px)",
            lineHeight: 1.1,
            textTransform: "uppercase",
            letterSpacing: "0.01em",
            color: "var(--dyna-fg)",
            margin: 0,
          }}
        >
          {fm.title}
        </h2>
        <p
          style={{
            fontFamily: "Geist, system-ui, sans-serif",
            fontSize: 16,
            lineHeight: 1.6,
            color: "var(--dyna-fg-muted)",
            margin: 0,
          }}
        >
          {fm.excerpt}
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            color: "var(--dyna-fg-muted)",
          }}
        >
          <span>{AUTHOR_NAME[fm.author]}</span>
          <span>·</span>
          <span>{dateLabel}</span>
          <span>·</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Clock size={11} />
            {t("pages.blog.readingTime", { minutes: article.readingTime })}
          </span>
        </div>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            marginTop: 6,
            color: "var(--lavender-300)",
            fontFamily: "Geist, system-ui, sans-serif",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {t("pages.blog.readArticle")}
          <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}
